import { Plus, UserPlus } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { ActionButtonType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { ITableAction } from 'core/types';
import { QuoteEntityType, QuoteListAction, QuoteListType } from 'quotes/enums';
import { Color, Variant } from 'theme/enums';

/**
 * Actions for Customer Quote/Order List
 *  - Create new Customer Quote/Order
 *  - Create a Prospect Quote (alternate-version companies, standalone Quotes list only)
 */
export const useActions = (
  entityType = QuoteEntityType.QUOTE,
  quoteListType = QuoteListType.GLOBAL
) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  const [activeActionDrawer, setActiveActionDrawer] = useState<QuoteListAction | null>(null);

  // This same list renders the Orders page and the Project/Job detail tabs; quoteListType is what
  // tells the embedded renders apart, so prospect quoting stays on the standalone Quotes page.
  const canCreateProspectQuote =
    AlternateVersionHelper.isAlternateVersionEnabled({ user }) &&
    QuoteEntityType.QUOTE === entityType &&
    QuoteListType.GLOBAL === quoteListType;

  const actions = (): ITableAction[] => [
    {
      type: ActionButtonType.BUTTON,
      label: t('buttonLabelAddQuote', { entityType }),
      color: Color.SUCCESS,
      variant: Variant.SOLID,
      startDecorator: <Plus />,
      onClick: () => setActiveActionDrawer(QuoteListAction.ADD_QUOTE),
    },
    ...(canCreateProspectQuote
      ? [
          {
            type: ActionButtonType.BUTTON,
            label: t('buttonLabelProspectQuote'),
            color: Color.NEUTRAL,
            variant: Variant.OUTLINED,
            startDecorator: <UserPlus />,
            onClick: () => setActiveActionDrawer(QuoteListAction.PROSPECT_QUOTE),
          },
        ]
      : []),
  ];

  return {
    activeActionDrawer,
    canCreateProspectQuote,
    onCloseActionDrawer: () => setActiveActionDrawer(null),
    actions,
    setActiveActionDrawer,
  };
};
